import { Link } from 'react-router-dom';
import { useCart } from '../../context/CartContext';
import { useTheme } from '../../context/ThemeContext';
import { useLang } from '../../context/LanguageContext';
import { useBranch } from '../../context/BranchContext';

export default function CartDrawer() {
  const {
    items,
    groups,
    drawerOpen,
    toastItem,
    closeDrawer,
    openDrawer,
    removeItem,
    updateQuantity,
    totalItems,
    totalPrice,
    clearCart,
    checkoutViaWhatsApp,
  } = useCart();
  const { isDark } = useTheme();
  const { t } = useLang();
  const { enabled: multiBranch } = useBranch();

  return (
    <>
      {/* ─── Add-to-cart toast ─── */}
      <div
        className={`fixed bottom-20 right-3 sm:bottom-24 sm:right-6 z-[60] transition-all duration-300 max-w-[calc(100vw-24px)] sm:max-w-xs ${
          toastItem ? 'opacity-100 translate-y-0 pointer-events-auto' : 'opacity-0 translate-y-4 pointer-events-none'
        }`}
      >
        <div className={`flex items-center gap-3 px-4 py-3 rounded-2xl shadow-xl border ${isDark ? 'bg-zinc-900 border-zinc-700' : 'bg-white border-zinc-200'}`}>
          <div className="w-7 h-7 flex items-center justify-center rounded-full bg-emerald-500/15 flex-shrink-0">
            <i className="ri-check-line text-emerald-500 text-sm" />
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-xs font-bold text-emerald-500">¡Agregado al carrito!</p>
            <p className={`text-[10px] truncate ${isDark ? 'text-zinc-400' : 'text-zinc-500'}`}>{toastItem}</p>
          </div>
          <button onClick={openDrawer} className="text-[10px] font-black text-red-500 uppercase tracking-wider whitespace-nowrap cursor-pointer hover:underline">
            Ver →
          </button>
        </div>
      </div>

      {/* ─── Backdrop ─── */}
      <div
        className={`fixed inset-0 bg-black/30 backdrop-blur-sm z-[70] transition-opacity duration-300 ${
          drawerOpen ? 'opacity-100 pointer-events-auto' : 'opacity-0 pointer-events-none'
        }`}
        onClick={closeDrawer}
      />

      {/* ─── Drawer panel ───────────────────────────────────────────────
           Mobile  : bottom sheet (slides up from bottom, max 88vh, rounded top)
           Desktop : right panel  (slides in from right, full height)
      ──────────────────────────────────────────────────────────────── */}
      <div
        className={`
          fixed bottom-0 left-0 right-0 max-h-[88vh] rounded-t-3xl
          sm:top-0 sm:right-0 sm:bottom-auto sm:left-auto sm:h-full sm:w-full sm:max-w-md sm:max-h-none sm:rounded-none
          z-[70] flex flex-col shadow-2xl border-t sm:border-t-0 sm:border-l
          transition-all duration-300 ease-out
          ${drawerOpen
            ? 'translate-y-0 sm:translate-x-0'
            : 'translate-y-full sm:translate-y-0 sm:translate-x-full'
          }
          ${isDark ? 'bg-zinc-950 border-zinc-800' : 'bg-white border-zinc-200'}
        `}
      >
        {/* ── Drag handle — mobile only ── */}
        <div className="sm:hidden flex justify-center pt-3 pb-0 flex-shrink-0">
          <div className={`w-10 h-1 rounded-full ${isDark ? 'bg-zinc-700' : 'bg-zinc-300'}`} />
        </div>

        {/* ── Header ── */}
        <div className={`flex items-center justify-between px-5 sm:px-6 py-3 sm:py-5 border-b flex-shrink-0 ${isDark ? 'border-zinc-800' : 'border-zinc-100'}`}>
          <div>
            <h2 className={`font-extrabold text-sm sm:text-base uppercase tracking-widest ${isDark ? 'text-white' : 'text-zinc-900'}`}>
              {t('cart_title')}
            </h2>
            <p className={`text-xs mt-0.5 ${isDark ? 'text-zinc-500' : 'text-zinc-400'}`}>
              {totalItems} {totalItems === 1 ? 'producto' : 'productos'}
            </p>
          </div>
          <button
            onClick={closeDrawer}
            className={`w-8 h-8 sm:w-9 sm:h-9 flex items-center justify-center rounded-full transition-colors cursor-pointer ${isDark ? 'bg-zinc-800 hover:bg-zinc-700' : 'bg-zinc-100 hover:bg-zinc-200'}`}
          >
            <i className={`ri-close-line text-base sm:text-lg ${isDark ? 'text-zinc-300' : 'text-zinc-600'}`} />
          </button>
        </div>

        {/* ── Items list ── */}
        <div className="flex-1 overflow-y-auto px-4 sm:px-6 py-3 sm:py-5">
          {items.length === 0 ? (
            <div className="flex flex-col items-center justify-center h-full text-center py-10 sm:py-16">
              <div className={`w-16 h-16 sm:w-20 sm:h-20 flex items-center justify-center rounded-full ${isDark ? 'bg-zinc-900' : 'bg-zinc-50'}`}>
                <i className={`ri-shopping-cart-line text-3xl sm:text-4xl ${isDark ? 'text-zinc-700' : 'text-zinc-300'}`} />
              </div>
              <p className={`text-sm mt-4 font-medium ${isDark ? 'text-zinc-400' : 'text-zinc-500'}`}>{t('cart_empty')}</p>
              <p className={`text-xs mt-1 ${isDark ? 'text-zinc-600' : 'text-zinc-400'}`}>Agrega productos del catálogo</p>
              <button onClick={closeDrawer} className="mt-5 text-xs font-bold text-red-600 uppercase tracking-wider cursor-pointer hover:underline whitespace-nowrap">
                {t('view_catalog')} →
              </button>
            </div>
          ) : (
            <div className="space-y-2.5 sm:space-y-3">
              {groups.map((group) => (
                <div key={group.branch.key} className="space-y-2.5 sm:space-y-3">
                  {/* Con sucursales, los productos se agrupan por donde se piden */}
                  {multiBranch && (
                    <p className={`flex items-center gap-1.5 pt-1 text-[11px] font-extrabold uppercase tracking-widest ${isDark ? 'text-zinc-300' : 'text-zinc-700'}`}>
                      <i className="ri-store-2-line" /> {group.branch.city}
                    </p>
                  )}
              {group.items.map((item) => (
                <div key={`${item.id}-${group.branch.key}`} className={`flex gap-3 p-3 rounded-2xl ${isDark ? 'bg-zinc-900' : 'bg-zinc-50'}`}>
                  {/* Product image */}
                  <div className={`w-16 h-16 sm:w-20 sm:h-20 rounded-xl overflow-hidden flex-shrink-0 border ${isDark ? 'bg-zinc-900 border-zinc-800' : 'bg-white border-zinc-200'}`}>
                    {item.images?.[0] ? (
                      <img src={item.images[0]} alt={item.name} className="w-full h-full object-contain p-1" />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center">
                        <i className={`ri-image-line text-xl ${isDark ? 'text-zinc-600' : 'text-zinc-300'}`} />
                      </div>
                    )}
                  </div>
                  {/* Product info */}
                  <div className="flex-1 min-w-0">
                    <p className={`text-xs font-semibold leading-snug line-clamp-2 mb-1 ${isDark ? 'text-white' : 'text-zinc-900'}`}>{item.name}</p>
                    {item.price ? (
                      <p className="text-red-600 font-extrabold text-sm mb-2">RD${(item.price * item.quantity).toLocaleString('es-DO')}</p>
                    ) : (
                      <p className={`text-xs italic mb-2 ${isDark ? 'text-zinc-500' : 'text-zinc-400'}`}>A consultar</p>
                    )}
                    <div className="flex items-center justify-between">
                      {/* Qty controls */}
                      <div className={`flex items-center gap-1 rounded-full px-1 py-0.5 border ${isDark ? 'bg-zinc-800 border-zinc-700' : 'bg-white border-zinc-200'}`}>
                        <button
                          onClick={() => updateQuantity(item.id, item.quantity - 1, item.branchKey)}
                          className={`w-6 h-6 flex items-center justify-center rounded-full transition-colors cursor-pointer text-sm font-bold ${isDark ? 'hover:bg-zinc-700 text-zinc-300' : 'hover:bg-zinc-100 text-zinc-700'}`}
                        >−</button>
                        <span className={`text-xs font-bold w-5 text-center ${isDark ? 'text-white' : 'text-zinc-900'}`}>{item.quantity}</span>
                        <button
                          onClick={() => updateQuantity(item.id, item.quantity + 1, item.branchKey)}
                          className={`w-6 h-6 flex items-center justify-center rounded-full transition-colors cursor-pointer text-sm font-bold ${isDark ? 'hover:bg-zinc-700 text-zinc-300' : 'hover:bg-zinc-100 text-zinc-700'}`}
                        >+</button>
                      </div>
                      {/* Delete */}
                      <button
                        onClick={() => removeItem(item.id, item.branchKey)}
                        className="w-7 h-7 sm:w-8 sm:h-8 flex items-center justify-center rounded-full hover:bg-red-50 transition-colors cursor-pointer"
                      >
                        <i className="ri-delete-bin-line text-red-400 text-sm" />
                      </button>
                    </div>
                  </div>
                </div>
              ))}
                </div>
              ))}
            </div>
          )}
        </div>

        {/* ── Footer CTA ── */}
        {items.length > 0 && (
          <div className={`px-4 sm:px-6 py-3 sm:py-5 border-t flex-shrink-0 ${isDark ? 'border-zinc-800 bg-zinc-950' : 'border-zinc-100 bg-white'}`}>
            {/* Total row */}
            <div className="flex items-center justify-between mb-3 sm:mb-4">
              <span className={`text-sm ${isDark ? 'text-zinc-400' : 'text-zinc-500'}`}>{t('cart_total')}</span>
              <span className={`text-lg sm:text-xl font-extrabold ${isDark ? 'text-white' : 'text-zinc-900'}`}>
                {totalPrice > 0 ? `RD$${totalPrice.toLocaleString('es-DO')}` : 'A consultar'}
              </span>
            </div>
            {/* WhatsApp checkout: uno por sucursal, cada uno a su numero */}
            {groups.length > 1 ? (
              <div className="space-y-2">
                {groups.map((group) => (
                  <button
                    key={group.branch.key}
                    onClick={() => checkoutViaWhatsApp(group.branch.key)}
                    className="w-full flex items-center justify-center gap-2 bg-emerald-500 hover:bg-emerald-600 text-white font-extrabold text-xs uppercase tracking-widest py-3.5 rounded-full transition-all duration-200 cursor-pointer whitespace-nowrap"
                  >
                    <i className="ri-whatsapp-line text-lg" />
                    Pedir en {group.branch.city}
                    {group.total > 0 && <span className="font-bold opacity-90">· RD${group.total.toLocaleString('es-DO')}</span>}
                  </button>
                ))}
              </div>
            ) : (
              <button
                onClick={() => checkoutViaWhatsApp()}
                className="w-full flex items-center justify-center gap-2 sm:gap-2.5 bg-emerald-500 hover:bg-emerald-600 text-white font-extrabold text-sm uppercase tracking-widest py-3.5 sm:py-4 rounded-full transition-all duration-200 cursor-pointer whitespace-nowrap"
              >
                <i className="ri-whatsapp-line text-lg sm:text-xl" />
                {t('cart_checkout')}
              </button>
            )}
            {/* Pagina completa del carrito */}
            <Link
              to="/carrito"
              onClick={closeDrawer}
              className="w-full mt-2 flex items-center justify-center gap-2 border-2 border-red-600 text-red-600 hover:bg-red-600 hover:text-[#ffffff] font-extrabold text-xs uppercase tracking-widest py-3 rounded-full transition-colors cursor-pointer whitespace-nowrap"
            >
              <i className="ri-shopping-cart-2-line" />
              Ver carrito completo
            </Link>
            {/* Clear */}
            <button
              onClick={clearCart}
              className={`w-full mt-2 text-center text-xs hover:text-red-500 transition-colors cursor-pointer py-2 uppercase tracking-widest font-medium whitespace-nowrap ${isDark ? 'text-zinc-600' : 'text-zinc-400'}`}
            >
              {t('cart_clear')}
            </button>
            <p className={`text-center text-[10px] mt-2 sm:mt-3 leading-relaxed ${isDark ? 'text-zinc-600' : 'text-zinc-400'}`}>
              Un agente SpartechRD recibirá tu pedido y te confirmará disponibilidad y precio final.
            </p>
          </div>
        )}
      </div>
    </>
  );
}
